import { useQuery } from '@tanstack/react-query';
import { ArrowRight, BookOpen, CheckSquare, CircleAlert, Inbox, PackageOpen, Search } from 'lucide-react';
import { Link } from 'react-router';
import type { ListResponse, TicketSummary } from '@aurora/contracts';
import { get, post, type ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { relativeTime } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Card, EmptyState, ErrorState, Skeleton } from '../components/ui';
import { useOpenPalette } from '../features/search/command-palette';
import { ticketKeys } from '../features/tickets/list-cache';
import { OPEN_CATEGORIES } from '../features/tickets/list-filters';
import { ticketStatusTone } from '../features/tickets/status-tone';

/**
 * Home, for somebody who is not on the desk.
 *
 * The agent dashboard used to be the only one, so a requester signed in to
 * tiles reading "Assigned to me: 0" and "Open across the desk", and a panel
 * headed "My queue" telling them nothing was assigned to them. None of that is
 * their job. A requester arrives with one of three intentions — something is
 * broken, they need something, or they want to know where their last request
 * got to — and this page is those three, in that order, plus help articles
 * that might mean they never need to raise anything at all.
 */
export function RequesterHome() {
  const { session, can } = useSession();
  const openPalette = useOpenPalette();
  const firstName = session?.user.name.split(' ')[0] ?? 'there';

  return (
    <div className="scrollbar-slim h-full overflow-y-auto">
      <div className="bg-sheen border-b border-line">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center sm:px-6">
          <h1 dir="auto" className="text-2xl font-semibold tracking-tight text-ink">
            Hi {firstName}, how can we help?
          </h1>
          <p className="mt-1.5 text-reading text-ink-muted">
            Search for an answer, report a problem, or ask for something you need.
          </p>
          <button
            type="button"
            onClick={openPalette}
            className={cn(
              'mx-auto mt-6 flex h-11 w-full max-w-xl items-center gap-2.5 rounded-panel border border-line-strong bg-surface px-4',
              'text-start text-reading text-ink-subtle shadow-raised transition-colors hover:border-ink-faint',
            )}
          >
            <Search aria-hidden className="size-4 shrink-0" />
            Search help articles and services…
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6">
        <div className="grid gap-3 sm:grid-cols-2">
          {can('ticket.create') && (
            <ActionCard
              to="/tickets/new"
              icon={CircleAlert}
              title="Report an issue"
              description="Something broken, slow or not working? Tell us and we’ll get it fixed."
            />
          )}
          {can('catalog.view') && (
            <ActionCard
              to="/catalog"
              icon={PackageOpen}
              title="Request something"
              description="Hardware, software, access and more, from the service catalog."
            />
          )}
        </div>

        {can('approval.view') && <PendingApprovals />}

        <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
          {can('ticket.view') && <MyOpenRequests />}
          {can('kb.view') && <PopularArticles />}
        </div>
      </div>
    </div>
  );
}

function ActionCard({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: typeof Inbox;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'group flex items-start gap-3 rounded-panel border border-line bg-surface p-4 shadow-raised',
        'transition-[border-color,box-shadow] hover:border-accent-line hover:shadow-float',
      )}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-card bg-accent-soft text-accent-ink">
        <Icon aria-hidden className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-reading font-semibold text-ink">
          {title}
          <ArrowRight
            aria-hidden
            className="size-3.5 text-ink-subtle transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
          />
        </span>
        <span className="mt-0.5 block text-body text-ink-muted">{description}</span>
      </span>
    </Link>
  );
}

function PendingApprovals() {
  const query = useQuery<{ data: unknown[] }, ApiError>({
    queryKey: ['approvals', 'mine'],
    queryFn: () => get('/approvals/mine'),
  });
  const count = query.data?.data.length ?? 0;
  if (count === 0) return null;

  return (
    <Link
      to="/approvals"
      className="flex items-center gap-3 rounded-card border border-caution-line bg-caution-soft px-4 py-3 transition-colors hover:brightness-[0.98]"
    >
      <CheckSquare aria-hidden className="size-4 shrink-0 text-caution-ink" />
      <span className="flex-1 text-body text-caution-ink">
        {count === 1 ? 'One request is waiting for your approval.' : `${count} requests are waiting for your approval.`}
      </span>
      <span className="text-mini font-medium text-caution-ink">Review</span>
    </Link>
  );
}

function MyOpenRequests() {
  const query = useQuery<ListResponse<TicketSummary>, ApiError>({
    // Under the shared ticket root, so a reply or a change of status made
    // anywhere in the app refreshes it — it used to sit under a key of its own
    // and show the old status until its cache happened to expire.
    queryKey: ticketKeys.panel('requester-open'),
    queryFn: () =>
      post<ListResponse<TicketSummary>>('/tickets/search', {
        filter: OPEN_CATEGORIES,
        sort: [{ field: 'last_activity_at', direction: 'desc' }],
        per_page: 5,
      }),
    // Somebody waiting on the desk leaves this open; it keeps itself current.
    refetchInterval: 60_000,
  });

  const total = query.data?.meta.total ?? 0;

  return (
    <section aria-labelledby="my-requests-heading">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 id="my-requests-heading" className="text-reading font-semibold text-ink">
          Your open requests
        </h2>
        <Link to="/tickets" className="text-mini font-medium text-accent-ink hover:underline">
          View all
        </Link>
      </div>
      <Card className="overflow-hidden">
        {query.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        ) : total === 0 ? (
          <EmptyState
            icon={<Inbox aria-hidden className="size-6" />}
            title="Nothing open"
            description="When you report an issue or ask for something, you can follow it here."
            className="py-10"
          />
        ) : (
          <ul className="divide-y divide-line">
            {query.data!.data.map((ticket) => (
              <li key={ticket.id}>
                <Link
                  to={`/tickets/${ticket.number}`}
                  className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-sunken"
                >
                  <div className="min-w-0 flex-1">
                    <p dir="auto" className="truncate text-body font-medium text-ink">
                      {ticket.subject}
                    </p>
                    <p className="mt-0.5 text-mini text-ink-subtle">
                      #{ticket.number}
                      {ticket.assignee ? (
                        <>
                          {' · With '}
                          {/* An Arabic name keeps its own direction inside the English line. */}
                          <bdi>{ticket.assignee.name}</bdi>
                        </>
                      ) : (
                        ' · Waiting to be picked up'
                      )}
                      {' · '}Updated {relativeTime(ticket.last_activity_at)}
                    </p>
                  </div>
                  <Badge tone={ticketStatusTone(ticket.status.category)} dot>
                    {/* Their wording — "Awaiting your response" — not the desk's "Pending". */}
                    {ticket.status.requester_label ?? ticket.status.label}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {total > 5 && (
        <p className="mt-2 text-mini text-ink-subtle">
          Showing the 5 most recently active of {total}.{' '}
          <Link to="/tickets" className="font-medium text-accent-ink hover:underline">
            See them all
          </Link>
        </p>
      )}
    </section>
  );
}

function PopularArticles() {
  const query = useQuery<ListResponse<{ id: number; title: string; summary: string | null }>, ApiError>({
    queryKey: ['requester-home', 'articles'],
    queryFn: () => get('/knowledge/articles', { per_page: 5, sort: '-view_count' }),
  });

  return (
    <section aria-labelledby="help-heading">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 id="help-heading" className="text-reading font-semibold text-ink">
          Popular help articles
        </h2>
        <Link to="/knowledge" className="text-mini font-medium text-accent-ink hover:underline">
          Browse all
        </Link>
      </div>
      <Card className="overflow-hidden">
        {query.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        ) : (query.data?.data.length ?? 0) === 0 ? (
          <EmptyState
            icon={<BookOpen aria-hidden className="size-6" />}
            title="No articles yet"
            description="Help articles your IT team publishes will appear here."
            className="py-10"
          />
        ) : (
          <ul className="divide-y divide-line">
            {query.data!.data.map((article) => (
              <li key={article.id}>
                <Link
                  to={`/knowledge/${article.id}`}
                  className="flex items-start gap-2.5 px-4 py-2.5 transition-colors hover:bg-sunken"
                >
                  <BookOpen aria-hidden className="mt-0.5 size-3.5 shrink-0 text-ink-subtle" />
                  <span className="min-w-0">
                    <span dir="auto" className="block truncate text-body text-ink">
                      {article.title}
                    </span>
                    {article.summary && (
                      <span dir="auto" className="block truncate text-mini text-ink-subtle">
                        {article.summary}
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
